import { useFocusEffect, useNavigation } from "@react-navigation/core";
import React, { useCallback, useContext, useMemo, useRef, useState } from "react";
import {
    Image,
    View,
    ScrollView,
    TouchableOpacity,
    TextInput,
    useWindowDimensions,
    Pressable,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { connect } from "react-redux";
import Colors from "../../styles/colors";
import { LoadingContext } from "../../context/LoadingContext";
import Api from "../../services/Api";
import StyleContext from "../../StyleContext";
import StandardText from "../../sharedComponents/standardText/StandardText";
import blurredSuitsBg from "./../../../assets/blurred_suits_bg.png";
import { fetchViewerFriends } from "../../redux/viewer/actions/friends";
import IconButton from "../../sharedComponents/iconButton/IconButton";
import ChevronIcon from "../../icons/ChevronIcon";
import { subscribeToChatUpdates, subscribeToGame } from "../../services/Wamp";
import AutocompleteInput from "react-native-autocomplete-input";
import { performAddTagToUserAccount } from "../../redux/auth/actions/login";
import { PresenceIndicator } from "../../game/components/PresenceIndicator";
import { UserMessage } from "../../sharedComponents/messages/UserMessage";
import GameHelper from "../../game/GameHelper";

const MessagesScreen = ({
    dispatch,
    accessToken,
    userAccount,
    subscription,
    route,
    friends,
}) => {
    const navigation = useNavigation();
    const loadingContext = useContext(LoadingContext);
    const styleContext = useContext(StyleContext);

    const { width, height } = useWindowDimensions();
    const [generalAppInfo, setGeneralAppInfo] = useState(null);
    const containerRef = useRef();
    const messagesScrollViewRef = useRef();
    const mounted = useRef(false);

    const [chats, setChatsUnordered] = useState([]);
    const [chatsLoading, setChatsLoading] = useState(false);
    const [chatsLoaded, setChatsLoaded] = useState(false);
    const [selectedChatId, setSelectedChatId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [messagesLoading, setMessagesLoading] = useState(false);
    const [availableFriends, setAvailableFriends] = useState([]);
    const [sendingMessages, setSendingMessages] = useState(false);
    const [newMessageContent, setNewMessageContent] = useState("");
    const [newChatRecipient, setNewChatRecipient] = useState("");
    const [newChatRecipientInputFocused, setNewChatRecipientInputFocused] = useState(false);
    const [newChatRecipientInputOpenedFromFocus, setNewChatRecipientInputOpenedFromFocus] = useState(false);
    const [chatTextInput, setChatTextInput] = useState(null);

    const [gameMessagesQueue, setGameMessagesQueue] = useState([]);
    const [currentGameMessage, setCurrentGameMessage] = useState(null);

    const initialRequestedChatStateRef = useRef(null);
    const [fromGameId, setFromGameId] = useState(null);

    const isSmallScreen = width < 800;
    const selectedChat = chats.find((chat) => chat.id === selectedChatId);
    const currentTurnPlayer = selectedChat && selectedChat.game && GameHelper.getCurrentTurnPlayer(selectedChat.game);
    // const allUnreadMessagesCount = chats.reduce((acc, c) => acc + (c.unreadMessagesCount || 0), 0);

    const setChats = (chats) => {
        const compareChats = (a, b) => {
            if (a.unreadMessagesCount > 0 && b.unreadMessagesCount <= 0)    return -1;
            if (a.unreadMessagesCount <= 0 && b.unreadMessagesCount > 0)    return  1;
            if (a.receivedLastMessageAt > b.receivedLastMessageAt)          return -1;
            if (a.receivedLastMessageAt < b.receivedLastMessageAt)          return  1;
            if (a.createdAt > b.createdAt)                                  return -1;
            if (a.createdAt < b.createdAt)                                  return  1;
            return 0;
        };

        if (typeof chats === "function") {
            setChatsUnordered(chatsInput => (chats(chatsInput) ?? []).sort(compareChats));
        } else {
            setChatsUnordered((chats ?? []).sort(compareChats));
        }
    };

    const getChatTitle = (chat) => {
        const otherParticipants = chat.participants.filter((p) => p.id != userAccount.id);
        if (chat.game) {
            return `Game #${chat.game.id}`;
        } else {
            return otherParticipants.map((p) => p.user.firstName).join(", ");
        }
    };

    const getChatSubtitle = (chat, parens) => {
        const otherParticipants = chat.participants.filter((p) => p.id != userAccount.id);
        let subtitle = "";
        if (chat.game) {
            subtitle = otherParticipants.map((p) => p.user.firstName).join(", ");
        } else if (otherParticipants.length === 1) {
            subtitle = otherParticipants[0].username;
        } else {
            subtitle = `${otherParticipants.length} people`;
        }

        if (parens && subtitle.length > 0) {
            return `(${subtitle})`;
        } else {
            return subtitle;
        }
    }

    const addGameMessageToQueue = useCallback((message) => {
        setGameMessagesQueue((current) => [...current, message]);
    }, [gameMessagesQueue]);

    useFocusEffect(useCallback(() => {
        if (currentGameMessage === null && gameMessagesQueue.length > 0) {
            const nextMessage = gameMessagesQueue[0];
            setCurrentGameMessage(nextMessage);
            setGameMessagesQueue((current) => current.slice(1));
        }
    }, [currentGameMessage, gameMessagesQueue]));

    useFocusEffect(useCallback(() => {
        setGameMessagesQueue([]);
        setCurrentGameMessage(null);
    }, [selectedChatId]));

    useFocusEffect(useCallback(() => {
        if (!currentGameMessage) {
            return;
        }

        const timeout = setTimeout(() => {
            setCurrentGameMessage(null);
        }, 3000);
        return () => { clearTimeout(timeout); };
    }, [currentGameMessage]));

    const newChatRecipientSuggestions = useMemo(() => {
        if (!newChatRecipientInputOpenedFromFocus) {
            return [];
        }

        const compareTo = newChatRecipient.toLowerCase();
        return availableFriends.filter((friend) => friend.username.toLowerCase().indexOf(compareTo) >= 0).slice(0, 15);
    }, [newChatRecipient, newChatRecipientInputOpenedFromFocus, availableFriends]);

    useFocusEffect(useCallback(() => {
        if (newChatRecipientInputFocused) {
            setNewChatRecipientInputOpenedFromFocus(true);
        } else if (newChatRecipientInputOpenedFromFocus) {
            let needsClearTimeout = true;
            const timeoutId = setTimeout(() => {
                setNewChatRecipientInputOpenedFromFocus(false);
                needsClearTimeout = false;
            }, 200);
            return () => {
                if (needsClearTimeout) {
                    clearTimeout(timeoutId);
                }
            };
        }
    }, [newChatRecipientInputFocused]));

    useFocusEffect(useCallback(() => {
        let requiresNavigationReset = false;

        if (route.params?.toUsername) {
            initialRequestedChatStateRef.current = {
                type: "username",
                username: route.params.toUsername
            };

            requiresNavigationReset = true;
        }
        
        if (route.params?.forceGotoUnreadChat) {
            initialRequestedChatStateRef.current = {
                type: "first-unread",
            };

            requiresNavigationReset = true;
        }

        if (route.params?.fromGame) {
            setFromGameId(route.params.fromGame);
            initialRequestedChatStateRef.current = {
                type: "game",
                gameId: route.params.fromGame,
            };
            requiresNavigationReset = true;
        }

        if (requiresNavigationReset) {
            navigation.setParams({
                fromGame: undefined,
                toUsername: undefined,
                forceGotoUnreadChat: undefined,
            });
        }
    }, [route.params, route.params?.fromGame, route.params?.toUsername]));

    useFocusEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    });

    const addChats = useCallback((...newChats) => {
        let chatMap = {};
        chats.forEach((c) => chatMap[c.id] = c);
        newChats.forEach((c) => chatMap[c.id] = c);
        setChats(Object.values(chatMap));
    }, [chats]);

    const loadChats = useCallback((initial) => {
        if (!accessToken || chatsLoading || (initial && chatsLoaded)) {
            return;
        }
        setChatsLoading(true);

        Api.getUserChats(accessToken).then((chats) => {
            addChats(...chats);
        }, (err) => {
            console.error("error loading chats", err);
        }).then(() => {
            setChatsLoaded(true);
            setChatsLoading(false);
        });
    }, [accessToken]);

    const markChatAsRead = useCallback((chatId) => {
        if (!accessToken) {
            return;
        }

        Api.markChatAsRead(accessToken, chatId);
    }, [accessToken, chats]);

    const loadMessages = useCallback(() => {
        if (!accessToken || !selectedChatId) {
            return;
        }

        setMessagesLoading(true);

        Api.userChatMessages(accessToken, selectedChatId, { before: null, limit: 50 }).then((messages) => {
            setMessages(messages);
            setTimeout(() => messagesScrollViewRef.current?.scrollToEnd({ animated: true }), 0);
        }, (err) => {
            console.error("error loading messages", err);
        }).then(() => {
            setMessagesLoading(false);
        });


    }, [accessToken, selectedChatId]);

    useFocusEffect(useCallback(() => {
        if (!chatsLoaded || !initialRequestedChatStateRef.current) {
            return;
        }

        if (initialRequestedChatStateRef.current.type === "game") {
            for (const chat of chats) {
                if (chat.game?.alphanumericId === initialRequestedChatStateRef.current.gameId) {
                    setSelectedChatId(chat.id);
                    break;
                }
            }
            initialRequestedChatStateRef.current = null;
        } else if (initialRequestedChatStateRef.current.type === "username") {
            handleOpenChat(initialRequestedChatStateRef.current.username);
            initialRequestedChatStateRef.current = null;
        } else if (initialRequestedChatStateRef.current.type === "first-unread") {
            let mostRecentUnread = null;
            for (const chat of chats) {
                if (chat.unreadMessagesCount <= 0) {
                    continue;
                }

                if (!mostRecentUnread) {
                    mostRecentUnread = chat;
                    continue;
                }

                if (chat.receivedLastMessageAt > mostRecentUnread.receivedLastMessageAt) {
                    mostRecentUnread = chat;
                }
            }

            if (mostRecentUnread) {
                setSelectedChatId(mostRecentUnread.id);
                initialRequestedChatStateRef.current = null;
            }
        }
    }, [chatsLoaded, chats]));

    useFocusEffect(
        useCallback(() => {
            const unsubFromChatUpdates = subscribeToChatUpdates(([{ type, chatId, messageId, unreadMessagesCount, receivedLastMessageAt }]) => {
                if (type === "new-message") {
                    chatId = typeof chatId === "number" ? chatId : Number.parseInt(chatId, 10);
                    messageId = typeof messageId === "number" ? messageId : Number.parseInt(messageId, 10);

                    setChats(chats => {
                        const updatedChatPosition = chats.findIndex((c) => c.id === chatId);
                        const updatedChat = updatedChatPosition >= 0 ? chats[updatedChatPosition] : null;

                        if (!updatedChat) {
                            loadChats();
                            return chats;
                        }

                        const newChat = { ...updatedChat, unreadMessagesCount, receivedLastMessageAt };

                        return [
                            ...chats.slice(0, updatedChatPosition),
                            newChat,
                            ...chats.slice(updatedChatPosition + 1),
                        ];
                    });

                    if (!Number.isSafeInteger(messageId)) {
                        return;
                    }

                    if (selectedChatId && chatId === selectedChatId) {
                        Api.userChatMessages(accessToken, selectedChatId, { after: messageId - 1, limit: 50 }).then((messages) => {
                            setMessages(messages);
                            markChatAsRead(selectedChatId);
                            setTimeout(() => messagesScrollViewRef.current?.scrollToEnd({ animated: true }), 0);
                        }, (err) => {
                            console.error("error loading messages", err);
                        }).then(() => {
                            setMessagesLoading(false);
                        });
                    }
                } else if (type === "chat-marked-as-read") {
                    chatId = typeof chatId === "number" ? chatId : Number.parseInt(chatId, 10);
                    setChats(chats => {
                        const updatedChatPosition = chats.findIndex((c) => c.id === chatId);
                        const updatedChat = updatedChatPosition >= 0 ? chats[updatedChatPosition] : null;

                        if (!updatedChat || updatedChat.unreadMessagesCount <= 0) {
                            return chats;
                        }

                        const newChat = { ...updatedChat, unreadMessagesCount: 0, receivedLastMessageAt };
                        return [
                            ...chats.slice(0, updatedChatPosition),
                            newChat,
                            ...chats.slice(updatedChatPosition + 1),
                        ];
                    });
                }
            });

            return () => {
                unsubFromChatUpdates();
            };
        }, [selectedChatId])
    );

    useFocusEffect(
        useCallback(() => {
            if (userAccount) {
                dispatch(fetchViewerFriends(null));
            }
        }, [dispatch, userAccount])
    );

    useFocusEffect(
        useCallback(() => {
            setAvailableFriends(friends.map((edge) => ({ ...edge.node })));
        }, [friends])
    );

    useFocusEffect(
        useCallback(() => {
            if (generalAppInfo) {
                loadingContext.setScreenLoading(false);
            }
        }, [generalAppInfo])
    );

    useFocusEffect(
        useCallback(() => {
            if (accessToken) {
                getInfo(accessToken);
                if (!userAccount?.tags?.includes('has-seen-messages')) {
                    dispatch(performAddTagToUserAccount(accessToken, 'has-seen-messages'));
                }
            }
        }, [accessToken])
    );

    const getInfo = async (accessToken) => {
        const generalAppInfoData = await Api.generalAppInfo(accessToken);
        setGeneralAppInfo(generalAppInfoData);
    };

    const handleUpdateCurrentChat = useCallback(() => {
        if (!accessToken || !selectedChatId) {
            return;
        }
        Api.getUserChat(accessToken, selectedChatId).then(
            (chat) => {
                setChats(chats => {
                    const updatedChatPosition = chats.findIndex((c) => c.id === chat.id);
                    if (updatedChatPosition < 0) {
                        return chats;
                    }
                    return [...chats.slice(0, updatedChatPosition), chat, ...chats.slice(updatedChatPosition + 1)];
                });
            },
            (err) => {
                console.error("there was an error updating the user chat", { selectedChatId }, err);
            }
        );
    }, [accessToken, selectedChatId, selectedChat]);

    useFocusEffect(useCallback(() => {
        if (!selectedChatId || !accessToken) {
            return;
        }
        const chat = chats.find((c) => c.id === selectedChatId);

        if (chat && chat.unreadMessagesCount > 0) {
            markChatAsRead(selectedChatId);
        }
    }, [accessToken, selectedChatId, chats]));


    useFocusEffect(
        useCallback(() => {
            if (accessToken) {
                loadChats(true);
            }
        }, [accessToken])
    );

    useFocusEffect(
        useCallback(() => {
            if (!accessToken) {
                return
            }
            loadMessages(selectedChatId);

            let unsub = null;
            const chat = chats.find((c) => c.id === selectedChatId);
            if (chat && chat.game?.id) {
                unsub = subscribeToGame(
                    chat.game.id,
                    GameHelper.getViewerPlayer(chat.game).id,
                    (args) => {
                        if (args[0] && args[0] === 'updated') {
                            handleUpdateCurrentChat();
                        }
                    },
                    (args) => {
                        if (args[0] && args[1] && args[0] === userAccount.id) {
                            addGameMessageToQueue(args[1]);
                        }
                    },
                );
            }

            return () => {
                if (unsub) {
                    unsub();
                }
            };
        }, [accessToken, selectedChatId])
    );

    const handleChatClicked = useCallback((chat) => {
        setSelectedChatId(chat.id);
        markChatAsRead(chat.id);
    }, [accessToken]);

    const handleSendMessage = useCallback(() => {
        if (!accessToken || !selectedChat) {
            return;
        }

        const messageContent = newMessageContent.trim();
        if (messageContent.length === 0) {
            return;
        }

        setNewMessageContent("");
        setTimeout(() => chatTextInput?.focus(), 0);

        setSendingMessages(true);

        Api.sendUserMessage(accessToken, selectedChat.id, messageContent).then((message) => {
            setMessages((current) => [...current, message]);
            setTimeout(() => messagesScrollViewRef.current?.scrollToEnd({ animated: true }), 0);
        }, (err) => {
            console.error("error sending message", err);
        }).then(() => {
            setSendingMessages(false);
        });
    }, [accessToken, selectedChatId, newMessageContent, chatTextInput]);

    const handleOpenChat = useCallback((username) => {
        setNewChatRecipient("");

        if (!accessToken) {
            return;
        }

        Api.openUserChat(accessToken, username).then((chat) => {
            const existingChat = chats.find((c) => c.id === chat.id);
            if (!existingChat) {
                addChats(chat);
                setSelectedChatId(chat.id);
            } else {
                setSelectedChatId(existingChat.id);
            }
        });
    }, [accessToken, chats]);

    return (
        <SafeAreaView
            style={[
                styleContext.safeArea,
                styleContext.container,
                {
                    flexDirection: "column",
                    justifyContent: "start",
                    alignItems: "stretch",
                },
            ]}
        >
            <View style={{ ...ownStyles.blurredSuitsBgContainer }}>
                <Image
                    source={{ uri: blurredSuitsBg }}
                    style={{ width: 300, height: 306 }}
                />
            </View>

            <View style={{ ...ownStyles.topBar }}>
                <View style={{display: "flex", flexDirection: 'row', alignItems: 'center', gap: 8, width: "100%"}}>
                    {isSmallScreen && selectedChatId && (
                        <View style={{
                            transform: [{ rotate: '180deg' }]
                        }}>
                            <IconButton icon={<ChevronIcon size={20} color={Colors.onBackground} />}
                                onPress={() => { setSelectedChatId(null) }} />
                        </View>
                    )}

                    {(!isSmallScreen || !selectedChatId) && fromGameId && (
                        <View style={{
                            transform: [{ rotate: '180deg' }]
                        }}>
                            <IconButton icon={<ChevronIcon size={20} color={Colors.onBackground} />}
                                onPress={() => { navigation.navigate('GameTable', { id: fromGameId  }) }} />
                        </View>
                    )}

                    <StandardText style={{ ...ownStyles.screenTitle }}>
                        Messages
                    </StandardText>

                    <View style={{ flex: 1 }} />

                    {fromGameId && (
                        <TouchableOpacity style={{...ownStyles.buttonBox}} onPress={() => {
                            navigation.navigate('GameTable', { id: fromGameId  })
                        }}>
                            <StandardText>
                                Back To Game
                            </StandardText>
                        </TouchableOpacity>
                    )}
                </View>
            </View>

            <ScrollView
                ref={containerRef}
                contentContainerStyle={{ height: "100%", justifyContent: "stretch" }}
            >
                <View style={{ ...ownStyles.mainContainer, ...(isSmallScreen ? {borderRadius: 0, borderLeftWidth: 0, borderRightWidth: 0} : {}) }}>
                    <View style={{
                        ...ownStyles.usersListContainer,
                        ...(isSmallScreen ? {width: 'auto', flex: 1} : {}),
                        ...(isSmallScreen && selectedChatId ? {display: 'none'} : {}) }}
                    >
                        <View style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 4, margin: 8, position: "relative", paddingTop: 64, backgroundColor: Colors.background }}>
                            <View style={ownStyles.autocompleteContainer}>
                                <AutocompleteInput
                                    onFocus={() => setNewChatRecipientInputFocused(true)}
                                    onBlur={() => setNewChatRecipientInputFocused(false)}
                                    style={[
                                        styleContext.input,
                                        { fontFamily: "Roboto_400Regular", flex: 1 },
                                    ]}
                                    placeholder="Start a new chat..."
                                    listContainerStyle={{
                                        backgroundColor: Colors.background,
                                        zIndex: 1,
                                        borderRadius: 4,
                                        borderWidth: 1,
                                        borderColor: Colors.onBackgroundBorder,
                                        maxHeight: 400,
                                    }}
                                    data={newChatRecipientSuggestions}
                                    value={newChatRecipient}
                                    onChangeText={(text) => setNewChatRecipient(text)}
                                    onSubmitEditing={() => handleOpenChat(newChatRecipient)}
                                    flatListProps={{
                                        keyExtractor: (_, idx) => idx.toString(),
                                        renderItem: ({ item }) => (
                                            <TouchableOpacity
                                                onPress={(e) => {e.preventDefault(); e.stopPropagation(); handleOpenChat(item.username);}}
                                                style={{
                                                    backgroundColor: Colors.background,
                                                    borderRadius: 4,
                                                    paddingHorizontal: 12,
                                                    paddingVertical: 8,
                                                }}
                                            >
                                                <StandardText>
                                                    {item.user?.firstName ?? item.firstName} ({item.username})
                                                </StandardText>
                                            </TouchableOpacity>
                                        ),
                                    }}
                                    hideResults={newChatRecipientSuggestions.length === 0}
                                />
                            </View>
                        </View>

                        <StandardText style={{ fontSize: 18, fontWeight: "bold", color: Colors.onBackground, paddingLeft: 16, paddingRight: 16, marginBottom: 24}}>
                            Chats
                        </StandardText>
                        {chats.length === 0 && <StandardText style={{ fontSize: 18, fontWeight: "bold", color: Colors.onBackground3, paddingLeft: 16, paddingRight: 16 }}>
                            No Open Chats
                        </StandardText>}

                        {chats.map((chat, index) => (
                            <TouchableOpacity key={index} style={{
                                padding: 16,
                                borderBottomColor: Colors.onBackgroundBorder,
                                borderBottomWidth: 1,
                                borderLeftColor: Colors.primary4,
                                borderLeftWidth: chat.id === selectedChatId ? 4 : 0,
                                zIndex: -1,
                            }}
                            onPress={() => handleChatClicked(chat)}
                        >
                                <View style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 4 }}>
                                    {/* <PresenceIndicator username={chat.recipient.username} /> */}
                                    <StandardText style={{}}>
                                        {getChatTitle(chat)}
                                    </StandardText>
                                    <StandardText style={{ color: Colors.onBackground3 }}>
                                        {getChatSubtitle(chat, true)}
                                    </StandardText>
                                    {chat.unreadMessagesCount > 0 && <View style={{marginLeft: 4, marginTop: 2, backgroundColor: Colors.secondary, borderRadius: 100, width: 20, height: 20, justifyContent: 'center', alignItems: 'center'}}>
                                        <StandardText style={{ fontSize: 10, color: Colors.onPrimary }}>
                                            {chat.unreadMessagesCount > 99 ? '99+' : chat.unreadMessagesCount}
                                        </StandardText>
                                    </View>}
                                </View>
                            </TouchableOpacity>
                        ))}
                    </View>

                    {selectedChat && (!isSmallScreen || selectedChatId) && <View style={{ ...ownStyles.chatMessagesContainer, paddingLeft: 16, paddingRight: 16 }}>
                        <View style={{ display: "flex", flexDirection: "column", justifyContent: "center", gap: 4, paddingTop: 24, paddingBottom: 24 }}>
                            {/* <PresenceIndicator username={selectedChat.recipient.username} /> */}
                            <StandardText style={{ fontSize: 20, fontWeight: "bold", color: Colors.onBackground }}>
                                {getChatTitle(selectedChat)}
                            </StandardText>
                            <StandardText Text style={{ color: Colors.onBackground3 }}>
                                {getChatSubtitle(selectedChat)}
                            </StandardText>
                            {currentTurnPlayer && (<StandardText Text style={{ color: Colors.onBackground3 }}>
                                Current Turn: {currentTurnPlayer.firstName}
                            </StandardText>)}
                        </View>
                        <View style={{ flex: 1, position: "relative" }}>
                            <ScrollView ref={messagesScrollViewRef}>
                                {messages.map((message, idx) => (<UserMessage
                                        key={message.id}
                                        sender={message.sender}
                                        content={message.content}
                                        createdAt={message.createdAt}
                                        isSender={message.isSender}
                                        firstInChain={message.sender?.username !== messages[idx - 1]?.sender?.username}
                                        />))}
                            </ScrollView>

                            {currentGameMessage && selectedChat?.game && (
                                <View style={{position: "absolute", top: 0, left: 0, right: 0}}>
                                    <Pressable
                                        onPress={() => setCurrentGameMessage(null)}
                                        style={{
                                            backgroundColor: 'rgba(30, 30, 30, 0.9)',
                                            justifyContent: 'center',
                                            borderRadius: 4,
                                            padding: 15,
                                        }}>
                                        <StandardText style={{ color: 'white', lineHeight: 30, fontWeight: 500 }}>{currentGameMessage}</StandardText>
                                    </Pressable>
                                </View>
                            )}

                        </View>
                        <View style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 6, marginTop: 16 }}>
                            <TextInput
                                ref={(input) => { setChatTextInput(input) }}
                                style={[
                                    styleContext.input,
                                    { fontFamily: "Roboto_400Regular", flex: 1, },
                                ]}
                                value={newMessageContent}
                                editable={!sendingMessages}
                                placeholder="Type a message..."
                                onChangeText={(text) => setNewMessageContent(text)}
                                onSubmitEditing={() => handleSendMessage()}
                            />
                            <IconButton icon={<ChevronIcon size={24} color={Colors.onBackground} />} onPress={() => handleSendMessage()} />
                        </View>
                    </View>}
                </View>
            </ScrollView>
        </SafeAreaView>
    );
};

const ownStyles = {
    topBar: {
        width: "100%",
        padding: 20,
        height: 82,
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
    },

    buttonBox: {
        borderWidth: 2,
        borderColor: Colors.onBackground,
        backgroundColor: Colors.background,
        padding: 7,
        borderRadius: 6,
        cursor: 'pointer'
    },

    screenTitle: {
        fontSize: 20,
        weight: 500,
        color: Colors.onBackground4,
    },

    blurredSuitsBgContainer: {
        position: "absolute",
        top: 0,
        left: 0,
        bottom: 0,
        right: 0,
        justifyContent: "center",
        alignItems: "center",
    },

    mainContainer: {
        display: "flex",
        flexDirection: "row",
        width: "100%",
        height: "100%",
        maxWidth: 1280,
        maxHeight: 912,
        marginLeft: "auto",
        marginRight: "auto",
        borderWidth: 1,
        borderColor: Colors.onBackgroundBorder,
        borderRadius: 4,

        shadowColor: "#000",
        shadowOffset: {
            width: 0,
            height: 2,
        },
        shadowOpacity: 0.23,
        shadowRadius: 2.62,

        elevation: 4,
    },

    usersListContainer: {
        display: "flex",
        flexDirection: "column",
        width: 288,
        borderRightWidth: 1,
        borderColor: Colors.onBackgroundBorder,
        paddingTop: 16,
    },

    chatMessagesContainer: {
        flex: 1,
        display: "flex",
        flexDirection: "column",
    },

    autocompleteContainer: {
        // Hack required to make the autocomplete
        // work on Andrdoid
        flex: 1,
        left: 0,
        position: 'absolute',
        right: 0,
        top: 0,
        zIndex: 1,
        padding: 5,
    },
    // message: {
    //     borderLeftWidth: 2,
    //     borderLeftColor: Colors.onBackground3,
    //     padding: 8,
    //     marginTop: 8,
    //     marginBottom: 8,
    // },

    // messageTime: {
    //     fontSize: 14,
    //     color: Colors.onBackground3,
    // },

    // messageSenderOrReceiverName: {
    //     fontWeight: "bold",
    // },
    // messageSenderOrReceiverUsername: {
    //     fontWeight: "bold",
    //     color: Colors.onBackground3,
    //     marginLeft: 4,
    // },

    // messageContent: {},

    // recipientMenuScrollView: {
    //     maxHeight: 300,

    //     backgroundColor: Colors.background,
    //     borderRadius: 3,
    //     borderWidth: 2,
    //     borderColor: Colors.onBackground,
    //     boxShadow: "rgb(0 0 0 / 20%) 3px 3px 4px",
    // },

    // recipientMenuOptions: {
    //     optionsContainer: {
    //         backgroundColor: "transparent",
    //         marginTop: 45,
    //         boxShadow: "none",
    //     },
    // },

    // recipientMenuOption: {
    //     optionWrapper: {
    //         backgroundColor: Colors.background,
    //         borderRadius: 0,
    //         borderBottomWidth: 2,
    //         padding: 10,
    //         borderColor: Colors.onBackground,
    //     },
    //     optionText: {
    //         color: Colors.onBackground,
    //     },
    // },
};

const mapStateToProps = (state) => {
    return {
        accessToken: state.auth.login.accessToken,
        userAccount: state.auth.login.userAccount,
        gamesError: state.viewer.games.error,
        friends: state.viewer.friends.data,
        friendsCursor: state.viewer.friends.cursor,
        friendsLoading: state.viewer.friends.loading,
        subscription: state.auth.profile.subscription,
    };
};

export default connect(mapStateToProps)(MessagesScreen);
